import {
  API_URL,
  authFetch,
  clearAuthStorage,
  getAccessToken,
  setAccessToken,
} from "../../components/utils/auth";

function buildAdminApiRoot(apiUrl) {
  const normalizedApiUrl = apiUrl.replace(/\/+$/, "");

  if (normalizedApiUrl.endsWith("/admin")) {
    return normalizedApiUrl;
  }

  return `${normalizedApiUrl}/admin`;
}

export const API_ROOT = buildAdminApiRoot(API_URL);

export class ApiError extends Error {
  constructor(message, options = {}) {
    super(message);
    this.name = "ApiError";
    this.status = options.status ?? null;
    this.fieldErrors = options.fieldErrors ?? {};
    this.details = options.details ?? null;
  }
}

export function getAdminToken() {
  return getAccessToken() || "";
}

export function setAdminToken(token) {
  setAccessToken(token);
}

export function clearAdminToken() {
  clearAuthStorage();
}

export function buildQuery(params = {}) {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, value);
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : "";
}

function normalizeFieldErrors(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return {};
  }

  return Object.entries(payload).reduce((fieldErrors, [key, value]) => {
    if (["detail", "non_field_errors"].includes(key)) {
      return fieldErrors;
    }

    if (Array.isArray(value)) {
      return { ...fieldErrors, [key]: value.join(" ") };
    }

    if (typeof value === "string") {
      return { ...fieldErrors, [key]: value };
    }

    return fieldErrors;
  }, {});
}

function buildErrorMessage(status, payload, fieldErrors) {
  if (Object.keys(fieldErrors).length) {
    return "Please correct the highlighted fields.";
  }

  if (payload?.detail) {
    return payload.detail;
  }

  if (status === 401) {
    return "Your session expired. Please sign in again.";
  }

  if (status === 403) {
    return "You do not have permission to perform this action.";
  }

  if (status === 404) {
    return "That record could not be found.";
  }

  if (status >= 500) {
    return "Something went wrong on the server. Please try again.";
  }

  return "Request failed. Please try again.";
}

async function parseResponsePayload(response) {
  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    return response.json().catch(() => null);
  }

  return response.text().catch(() => "");
}

export async function adminApi(path, options = {}) {
  const headers = {
    Accept: "application/json",
    ...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
    ...options.headers,
  };

  let response;

  try {
    response = await authFetch(`${API_ROOT}${path}`, {
      ...options,
      headers,
    });
  } catch (networkError) {
    throw new ApiError("Network request failed. Check your connection and try again.", {
      details: networkError,
    });
  }

  const payload = await parseResponsePayload(response);

  if (!response.ok) {
    const fieldErrors = normalizeFieldErrors(payload);
    throw new ApiError(buildErrorMessage(response.status, payload, fieldErrors), {
      status: response.status,
      fieldErrors,
      details: payload,
    });
  }

  return payload;
}